import { EventDay } from '@app/services/event-config.service';

/**
 * Parsers that turn an uploaded/pasted legacy config file into one normalized
 * shape the event-config import can map onto the form. Three input formats are
 * supported:
 *   - JSON     api/webapp client config files
 *   - .ts      Angular environment.*.ts (export const environment = { ... })
 *   - .php     legacy *_conf.inc.php ($_VAR = "...";)
 *
 * Everything is best-effort: only recognized values are emitted, and parsing
 * never throws to the caller (errors surface as an empty result + message).
 */

export type ImportFormat = 'json' | 'ts' | 'php' | 'unknown';

export interface NormalizedImport {
  event: {
    title?: string;
    year?: string;
    www_url?: string;
    email?: string;
    frontend_url?: string;
    welcome_heading?: string;
    welcome?: string;
    mail_subject?: string;
    complete_profile_by?: string;
    validate_registration_latest?: string;
  };
  logistics: {
    hotel?: string;
    event_space?: string;
    event_date?: string;
    book_night?: string;
    cancel_date?: string;
    day1?: string;
    day2?: string;
  };
  media: {
    hero_video_mp4?: string;
    hero_video_webm?: string;
    pdf_logo?: string;
    pdf_footer?: string;
  };
  flags: {
    booking_stop?: boolean;
    show_gallery?: boolean;
    exhibitor_booking_active?: boolean;
    test_version?: boolean;
  };
  notifications: { [key: string]: boolean };
  testmail: { visitor?: string; exhibitor?: string; atout?: string };
  languages?: string[];
  days?: EventDay[];
  activities?: { [lang: string]: string[] };
  categories?: string[];
  /** event/dynamic-fields.json shape, or null when the source has no dyn fields */
  dynamicFields?: any | null;
  /** event/booking-restrictions.json shape, or null when none present */
  bookingRestrictions?: any | null;
}

const AVAILABLE_LANG_CODES = ['de', 'fr', 'en'];

/** Maps a dynamic-field variable suffix to its (section, group) destination. */
const DYN_FIELD_GROUPS: { suffix: string; section: 'visitor' | 'exhibitor'; group: string }[] = [
  { suffix: 'identity', section: 'visitor', group: 'identity' },
  { suffix: 'programming', section: 'visitor', group: 'programming' },
  { suffix: 'products', section: 'visitor', group: 'products' },
  { suffix: 'regions', section: 'visitor', group: 'regions' },
  { suffix: 'productsreq', section: 'visitor', group: 'productsreq' },
  { suffix: 'regions_ex', section: 'exhibitor', group: 'regions' },
  { suffix: 'activities_ex', section: 'exhibitor', group: 'activities' },
];

// --------------------------------------------------------------------------
// Format detection
// --------------------------------------------------------------------------

export function detectFormat(fileName: string, text: string): ImportFormat {
  const name = (fileName || '').toLowerCase();
  if (name.endsWith('.json')) return 'json';
  if (name.endsWith('.ts')) return 'ts';
  if (name.endsWith('.php') || name.endsWith('.inc')) return 'php';

  // content sniff (covers pasted text without a filename)
  const t = (text || '').trim();
  if (t.startsWith('{') || t.startsWith('[')) return 'json';
  if (/export\s+const\s+environment/.test(t)) return 'ts';
  if (t.startsWith('<?') || /\$_[A-Z]/.test(t)) return 'php';
  return 'unknown';
}

// --------------------------------------------------------------------------
// .ts environment parser
// --------------------------------------------------------------------------

/**
 * Extract the `environment` object literal from an Angular environment.*.ts and
 * evaluate it into a plain object. TS-only syntax (type casts, env[...] refs) is
 * stripped first. Runs in the superuser-only admin tool; wrapped in try/catch.
 */
export function parseTsEnvironment(text: string): any {
  try {
    const start = text.indexOf('export const environment');
    if (start < 0) return null;
    const braceStart = text.indexOf('{', start);
    if (braceStart < 0) return null;

    // brace-match to the closing } (ignoring braces inside strings)
    let depth = 0;
    let end = -1;
    let quote: string | null = null;
    for (let i = braceStart; i < text.length; i++) {
      const ch = text[i];
      const prev = text[i - 1];
      if (quote) {
        if (ch === quote && prev !== '\\') quote = null;
        continue;
      }
      if (ch === '"' || ch === "'" || ch === '`') {
        quote = ch;
        continue;
      }
      if (ch === '{') depth++;
      else if (ch === '}') {
        depth--;
        if (depth === 0) {
          end = i;
          break;
        }
      }
    }
    if (end < 0) return null;

    let body = text.slice(braceStart, end + 1);
    // strip TS type casts: "as string[]", "as boolean[]", "as Record<...>", "as Foo"
    body = body.replace(/\s+as\s+(?:Record<[^>]*>|[A-Za-z0-9_]+(?:\[\])?)/g, '');
    // replace env[...] references (e.g. version) with a literal
    body = body.replace(/env\[[^\]]*\]/g, "'imported'");

    // eslint-disable-next-line no-new-func
    const fn = new Function('return (' + body + ');');
    return fn();
  } catch {
    return null;
  }
}

// --------------------------------------------------------------------------
// .php conf parser
// --------------------------------------------------------------------------

/**
 * Parse a legacy *_conf.inc.php into a raw object keyed with canonical names the
 * normalizer understands. Block and full-line comments are removed first so
 * commented-out assignments are ignored.
 */
export function parsePhpConf(text: string): any {
  // drop block comments, then full-line // and # comments
  const noBlocks = text.replace(/\/\*[\s\S]*?\*\//g, '');
  const clean = noBlocks
    .split(/\r?\n/)
    .filter((l) => !/^\s*(\/\/|#)/.test(l))
    .join('\n');

  const raw: any = {};

  const str = (name: string): string | undefined => {
    const m = clean.match(new RegExp('\\$' + name + '\\s*=\\s*(?:"([^"]*)"|\'([^\']*)\')'));
    if (!m) return undefined;
    return m[1] !== undefined ? m[1] : m[2];
  };
  const bool = (name: string): boolean | undefined => {
    const m = clean.match(new RegExp('\\$' + name + '\\s*=\\s*(true|false)\\s*;'));
    if (!m) return undefined;
    return m[1] === 'true';
  };

  // scalars
  raw._EVENT_TITLE = str('_EVENT_TITLE');
  raw._EVENT_YEAR = str('_EVENT_YEAR');
  raw._EVENT_DOMAIN = str('_EVENT_DOMAIN');
  raw._EVENT_INFOMAIL = str('_EVENT_INFOMAIL');
  raw._EVENT_MAIL_SUBJECT = str('_EVENT_MAIL_SUBJECT');
  raw._COMPLETE_PROFILE_BY = str('_COMPLETE_PROFILE_BY');
  raw._VALIDATE_REGISTRATION_LATEST = str('_VALIDATE_REGISTRATION_LATEST');

  raw._HOTEL_INFO = str('_HOTEL_INFO');
  raw._EVENT_SPACE = str('_EVENT_SPACE');
  raw._EVENT_DATE = str('_EVENT_DATE');
  raw._BOOK_NIGHT = str('_BOOK_NIGHT');
  raw._CANCEL_DATE = str('_CANCEL_DATE');
  raw._DAY_1 = str('_DAY_1');
  raw._DAY_2 = str('_DAY_2');

  raw._TEST_MAIL_VISITOR = str('_TEST_MAIL_VISITOR');
  raw._TEST_MAIL_EXHIBITOR = str('_TEST_MAIL_EXHIBITOR');
  raw._TEST_MAIL_ATOUT = str('_TEST_MAIL_ATOUT');

  // flags
  raw._BOOKING_STOP = bool('_BOOKING_STOP');
  raw._EVENT_SHOW_GALLERY = bool('_EVENT_SHOW_GALLERY');
  raw._EXHIBITOR_BOOKING_ACTIVE = bool('_EXHIBITOR_BOOKING_ACTIVE');
  raw._TEST_VERSION = bool('_TEST_VERSION');

  // notifications
  for (const k of [
    'cancel_appointment',
    'create_appointment',
    'password_lost',
    'register_new',
    'approve_visitor',
    'activate_visitor',
    'approve_exhibitor',
    'activate_exhibitor',
    'sendaccountinfo',
    'sendreminder',
  ]) {
    const v = bool('_info_' + k);
    if (v !== undefined) raw['_info_' + k] = v;
  }

  // event cities -> event_cities[]
  const cityMap: { [idx: number]: any } = {};
  const cityRe = /\$_EVENT_CITIES\[(\d+)\]\['(\w+)'\]\s*=\s*(?:"([^"]*)"|'([^']*)'|(\d+))/g;
  let cm: RegExpExecArray | null;
  while ((cm = cityRe.exec(clean)) !== null) {
    const idx = +cm[1];
    const field = cm[2];
    const val = cm[3] !== undefined ? cm[3] : cm[4] !== undefined ? cm[4] : cm[5];
    cityMap[idx] = cityMap[idx] || { id: idx };
    cityMap[idx][field] = field === 'interval' || field === 'id' ? +(val as string) : val;
  }
  const cities = Object.keys(cityMap)
    .map((k) => +k)
    .sort((a, b) => a - b)
    .map((k) => cityMap[k]);
  if (cities.length) raw.event_cities = cities;

  // Per-day booking restrictions (only ACTIVE / uncommented lines, since the
  // comment stripping above already dropped disabled ones). na_* = per-visitor
  // blackout (visitor-id keyed), nb_* = per-country no-booking times.
  const booking: { [idx: number]: any } = {};
  const ensureB = (idx: number) => (booking[idx] = booking[idx] || {});
  // single-bracket fields were captured into cityMap above
  for (const k of Object.keys(cityMap)) {
    const idx = +k;
    const c = cityMap[idx];
    if (c.na_caption !== undefined) ensureB(idx).na_caption = c.na_caption;
    if (c.na_ids !== undefined) ensureB(idx).na_ids = c.na_ids;
    if (c.nb_countries !== undefined) ensureB(idx).nb_countries = c.nb_countries;
  }
  // na_time[visitorId] — visitor id is an int key
  const naTimeRe = /\$_EVENT_CITIES\[(\d+)\]\['na_time'\]\[(\d+)\]\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
  let ntm: RegExpExecArray | null;
  while ((ntm = naTimeRe.exec(clean)) !== null) {
    const idx = +ntm[1];
    ensureB(idx).na_time = ensureB(idx).na_time || {};
    booking[idx].na_time[ntm[2]] = ntm[3] !== undefined ? ntm[3] : ntm[4];
  }
  // nb_time[country] — country code is a quoted string key
  const nbTimeRe = /\$_EVENT_CITIES\[(\d+)\]\['nb_time'\]\[\s*(?:"([^"]*)"|'([^']*)')\s*\]\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
  let nbm: RegExpExecArray | null;
  while ((nbm = nbTimeRe.exec(clean)) !== null) {
    const idx = +nbm[1];
    const country = (nbm[2] !== undefined ? nbm[2] : nbm[3]) as string;
    ensureB(idx).nb_time = ensureB(idx).nb_time || {};
    booking[idx].nb_time[country] = nbm[4] !== undefined ? nbm[4] : nbm[5];
  }
  if (Object.keys(booking).length) raw.booking_cities = booking;

  // activities per language
  const actRe = /\$_activities_(en|fr)\[(\d+)\]\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
  let am: RegExpExecArray | null;
  const acts: { [lang: string]: string[] } = {};
  while ((am = actRe.exec(clean)) !== null) {
    const lang = am[1];
    const i = +am[2];
    const val = am[3] !== undefined ? am[3] : am[4];
    acts[lang] = acts[lang] || [];
    acts[lang][i] = val as string;
  }
  if (acts['en']) raw.activities_en = acts['en'].map((x) => x || '');
  if (acts['fr']) raw.activities_fr = acts['fr'].map((x) => x || '');

  // exhibitor categories list
  const catMatch = clean.match(/\$_EVENT_EXIBITOR_CATEGORIES\s*=\s*\[([\s\S]*?)\]\s*;/);
  if (catMatch) raw.EVENT_CATEGORIES = parsePhpStringList(catMatch[1]);

  // Slim API settings (defaults.php / settings.php): $settings['event'] = [...];
  // and $settings['smtp'] = [...]; — extract the string 'key' => 'value' pairs.
  const settingsBlock = (key: string): { [k: string]: string } | null => {
    const m = clean.match(new RegExp("\\$settings\\['" + key + "'\\]\\s*=\\s*\\[([\\s\\S]*?)\\]\\s*;"));
    return m ? parsePhpAssoc(m[1]) : null;
  };
  const evSettings = settingsBlock('event');
  if (evSettings) {
    raw.event = {
      title: evSettings['title'],
      www_url: evSettings['www_url'],
      email: evSettings['email'],
    };
  }
  const smtpSettings = settingsBlock('smtp');
  if (smtpSettings && smtpSettings['default_email']) {
    raw.smtp = { default_email: smtpSettings['default_email'] };
  }

  // languages list ($languages[n]['code'] = "xx")
  const langRe = /\$languages\[\d+\]\['code'\]\s*=\s*(?:"([^"]*)"|'([^']*)')/g;
  let lm: RegExpExecArray | null;
  const langs: string[] = [];
  while ((lm = langRe.exec(clean)) !== null) {
    langs.push((lm[1] !== undefined ? lm[1] : lm[2]) as string);
  }
  if (langs.length) raw._languages = langs;

  // dynamic fields: $show_dyn_X = [...]; and $caption_dyn_X = [ 'k' => 'v', ... ];
  for (const def of DYN_FIELD_GROUPS) {
    const showMatch = clean.match(new RegExp('\\$show_dyn_' + def.suffix + '\\s*=\\s*\\[([\\s\\S]*?)\\]\\s*;'));
    const capMatch = clean.match(new RegExp('\\$caption_dyn_' + def.suffix + '\\s*=\\s*\\[([\\s\\S]*?)\\]\\s*;'));
    if (showMatch) raw['show_dyn_' + def.suffix] = parsePhpBoolList(showMatch[1]);
    if (capMatch) raw['caption_dyn_' + def.suffix] = parsePhpAssoc(capMatch[1]);
  }

  return raw;
}

/** Parse a PHP list body of quoted strings: `"a", "b", 'c'`. */
function parsePhpStringList(body: string): string[] {
  const out: string[] = [];
  const re = /(?:"([^"]*)"|'([^']*)')/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    out.push((m[1] !== undefined ? m[1] : m[2]) as string);
  }
  return out;
}

/** Parse a PHP list body of booleans: `true, false, true`. */
function parsePhpBoolList(body: string): boolean[] {
  return body
    .replace(/\/\/[^\n]*/g, '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s === 'true' || s === 'false')
    .map((s) => s === 'true');
}

/** Parse a PHP assoc body: `'k' => 'v', "k2" => "v2"` -> { k: v, k2: v2 }. */
function parsePhpAssoc(body: string): { [key: string]: string } {
  const out: { [key: string]: string } = {};
  const re = /(?:"([^"]*)"|'([^']*)')\s*=>\s*(?:"([^"]*)"|'([^']*)')/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body)) !== null) {
    const key = (m[1] !== undefined ? m[1] : m[2]) as string;
    const val = m[3] !== undefined ? m[3] : m[4];
    out[key] = (val as string) || '';
  }
  return out;
}

// --------------------------------------------------------------------------
// Normalization (single mapper for all formats)
// --------------------------------------------------------------------------

const firstOf = (...vals: any[]) => vals.find((v) => v !== undefined && v !== null && v !== '');

/**
 * Convert a legacy date string to the ISO `yyyy-mm-dd` an <input type="date">
 * expects. Accepts `DD.MM.YYYY` / `DD/MM/YYYY` (and already-ISO values).
 * Returns null when the value isn't a plain single date.
 */
function toIsoDate(value: any): string | null {
  if (typeof value !== 'string') return null;
  const s = value.trim();
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return s;
  m = s.match(/^(\d{1,2})[.\/](\d{1,2})[.\/](\d{4})$/);
  if (!m) return null;
  const day = m[1].padStart(2, '0');
  const month = m[2].padStart(2, '0');
  if (+month < 1 || +month > 12 || +day < 1 || +day > 31) return null;
  return `${m[3]}-${month}-${day}`;
}

/** Parse + normalize an uploaded/pasted file into the shared import shape. */
export function parseImport(fileName: string, text: string): { format: ImportFormat; data: NormalizedImport | null } {
  const format = detectFormat(fileName, text);
  let raw: any = null;

  if (format === 'json') {
    try {
      raw = JSON.parse(text);
    } catch {
      raw = null;
    }
  } else if (format === 'ts') {
    raw = parseTsEnvironment(text);
  } else if (format === 'php') {
    raw = parsePhpConf(text);
  }

  if (!raw) return { format, data: null };
  return { format, data: normalize(raw) };
}

/** Map a raw object (any source) onto the normalized import shape. */
export function normalize(raw: any): NormalizedImport {
  const ev = raw.event || {};
  const acc = raw.account_info || {};
  const pdf = raw.pdf || {};

  const out: NormalizedImport = {
    event: {},
    logistics: {},
    media: {},
    flags: {},
    notifications: {},
    testmail: {},
  };

  // ---- event ----
  out.event.title = firstOf(ev.title, raw.txt_EventName, raw._EVENT_TITLE);
  out.event.year = firstOf(ev.year, raw.event_year, raw._EVENT_YEAR);
  out.event.www_url = firstOf(ev.www_url, raw.txt_Event_Domain, raw._EVENT_DOMAIN);
  out.event.email = firstOf(ev.email, raw.smtp && raw.smtp.default_email, raw._EVENT_INFOMAIL);
  out.event.frontend_url = firstOf(ev.frontend_url);
  out.event.welcome_heading = firstOf(raw.txt_Welcome);
  out.event.welcome = firstOf(raw.txt_Welcome_Text);
  out.event.mail_subject = firstOf(raw._EVENT_MAIL_SUBJECT);
  out.event.complete_profile_by = firstOf(raw._COMPLETE_PROFILE_BY);
  out.event.validate_registration_latest = firstOf(raw._VALIDATE_REGISTRATION_LATEST);

  // ---- logistics ----
  out.logistics.hotel = firstOf(acc.hotel, raw.hotel_info, raw._HOTEL_INFO);
  out.logistics.event_space = firstOf(acc.event_space, raw.event_space, raw._EVENT_SPACE);
  out.logistics.event_date = firstOf(acc.event_date, raw.event_date, raw._EVENT_DATE);
  out.logistics.book_night = firstOf(acc.book_night, raw.book_night, raw._BOOK_NIGHT);
  out.logistics.cancel_date = firstOf(acc.cancel_date, raw.cancel_date, raw._CANCEL_DATE);
  out.logistics.day1 = firstOf(acc.day1, raw.day_1, raw._DAY_1);
  out.logistics.day2 = firstOf(acc.day2, raw.day_2, raw._DAY_2);

  // ---- media ----
  out.media.hero_video_mp4 = firstOf(raw.video_source_mp4);
  out.media.hero_video_webm = firstOf(raw.video_source_webm);
  out.media.pdf_logo = firstOf(pdf.logo);
  out.media.pdf_footer = firstOf(pdf.footer_image);

  // ---- flags ----
  if (raw._BOOKING_STOP !== undefined) out.flags.booking_stop = !!raw._BOOKING_STOP;
  if (firstOf(raw._EVENT_SHOW_GALLERY, raw.show_gallery) !== undefined && (raw._EVENT_SHOW_GALLERY !== undefined || raw.show_gallery !== undefined)) {
    out.flags.show_gallery = !!firstOf(raw._EVENT_SHOW_GALLERY, raw.show_gallery);
  }
  if (raw._EXHIBITOR_BOOKING_ACTIVE !== undefined) out.flags.exhibitor_booking_active = !!raw._EXHIBITOR_BOOKING_ACTIVE;
  if (raw._TEST_VERSION !== undefined) out.flags.test_version = !!raw._TEST_VERSION;

  // ---- notifications ----
  for (const k of [
    'cancel_appointment',
    'create_appointment',
    'password_lost',
    'register_new',
    'approve_visitor',
    'activate_visitor',
    'approve_exhibitor',
    'activate_exhibitor',
    'sendaccountinfo',
    'sendreminder',
  ]) {
    if (raw['_info_' + k] !== undefined) out.notifications[k] = !!raw['_info_' + k];
  }

  // ---- test mail recipients ----
  out.testmail.visitor = firstOf(raw._TEST_MAIL_VISITOR);
  out.testmail.exhibitor = firstOf(raw._TEST_MAIL_EXHIBITOR);
  out.testmail.atout = firstOf(raw._TEST_MAIL_ATOUT);

  // ---- languages ----
  const langs: string[] = [];
  const pushLang = (code: string) => {
    const c = (code || '').slice(0, 2).toLowerCase();
    if (AVAILABLE_LANG_CODES.includes(c) && !langs.includes(c)) langs.push(c);
  };
  if (Array.isArray(raw.supportedLanguages)) raw.supportedLanguages.forEach(pushLang);
  if (raw.veranstaltungs_land) pushLang(raw.veranstaltungs_land);
  if (Array.isArray(raw._languages)) raw._languages.forEach(pushLang);
  if (langs.length) out.languages = langs;

  // ---- agenda days (from event_cities) ----
  if (Array.isArray(raw.event_cities) && raw.event_cities.length) {
    out.days = raw.event_cities.map((c: any) => ({
      day_no: +(c.id || 0),
      enabled: 1,
      caption: c.caption || '',
      // Legacy configs keep the date in the caption (DD.MM.YYYY); derive the
      // ISO date the <input type="date"> needs so the Date field gets filled.
      event_date: toIsoDate(c.event_date) || toIsoDate(c.caption),
      slot_start: c.start || '09:00',
      slot_end: c.end || '17:00',
      slot_interval: +(c.interval || 20),
      breaks: c.break || '',
      break_caption: c.break_caption || '',
      spots: 0,
    }));
  }

  // ---- activities (PHP only) ----
  const activities: { [lang: string]: string[] } = {};
  if (Array.isArray(raw.activities_en)) activities['en'] = raw.activities_en.slice();
  if (Array.isArray(raw.activities_fr)) activities['fr'] = raw.activities_fr.slice();
  if (Object.keys(activities).length) out.activities = activities;

  // ---- exhibitor categories ----
  if (Array.isArray(raw.EVENT_CATEGORIES) && raw.EVENT_CATEGORIES.length) {
    out.categories = raw.EVENT_CATEGORIES.slice();
  }

  // ---- dynamic profile fields ----
  out.dynamicFields = normalizeDynamicFields(raw);

  // ---- per-day booking restrictions (event/booking-restrictions.json) ----
  out.bookingRestrictions = normalizeBookingRestrictions(raw);

  return out;
}

/**
 * Build the event/booking-restrictions.json shape from any active na_ / nb_
 * entries captured per city. Enabled is set true so imported rules take effect
 * (matching the legacy conf, where these lines applied directly). Null when none.
 */
function normalizeBookingRestrictions(raw: any): any | null {
  if (!raw.booking_cities || !Object.keys(raw.booking_cities).length) return null;
  const cities: any = {};
  for (const idx of Object.keys(raw.booking_cities)) {
    const b = raw.booking_cities[idx];
    const entry: any = {};
    if (b.na_caption !== undefined) entry.na_caption = b.na_caption;
    if (b.na_ids !== undefined) entry.na_ids = b.na_ids;
    if (b.na_time && Object.keys(b.na_time).length) entry.na_time = b.na_time;
    if (b.nb_countries !== undefined) entry.nb_countries = b.nb_countries;
    if (b.nb_time && Object.keys(b.nb_time).length) entry.nb_time = b.nb_time;
    if (Object.keys(entry).length) cities[idx] = entry;
  }
  if (!Object.keys(cities).length) return null;
  return { enabled: true, cities };
}

/**
 * Build the event/dynamic-fields.json shape from the show_dyn_ and caption_dyn_
 * variables (present in both .ts and .php sources). Returns null when none found.
 */
function normalizeDynamicFields(raw: any): any | null {
  const result: any = { visitor: {}, exhibitor: {} };
  let found = false;

  for (const def of DYN_FIELD_GROUPS) {
    const show = raw['show_dyn_' + def.suffix];
    const captions = raw['caption_dyn_' + def.suffix];
    if (show === undefined && captions === undefined) continue;
    found = true;
    result[def.section][def.group] = {
      show: Array.isArray(show) ? show.map((v: any) => !!v) : [],
      captions: captions && typeof captions === 'object' ? captions : {},
    };
  }

  if (!found) return null;
  // drop empty sections
  if (!Object.keys(result.visitor).length) delete result.visitor;
  if (!Object.keys(result.exhibitor).length) delete result.exhibitor;
  return result;
}
